import paginatedTreeQuery from 'shared_queries/repository/paginated_tree.query.graphql';
import { createAlert } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/alert.js?vue3';
import { TREE_PAGE_SIZE, TREE_PAGE_LIMIT, COMMIT_BATCH_SIZE, GITALY_UNAVAILABLE_CODE, LOCK_UPDATED_EVENT, i18n } from '../constants';
import eventHub from '../event_hub';
import getRefMixin from '../mixins/get_ref';
import { getRefType } from '../utils/ref_type';
import projectPathQuery from '../queries/project_path.query.graphql';
import { readmeFile } from '../utils/readme';
import { loadCommits, reloadCommits, isRequested, resetRequestedCommits } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/repository/commits_service.js?vue3';
import FilePreview from '/builds/gitlab/gitlab-ee/app/assets/javascripts/repository/components/preview/index.vue?vue3';
import FileTable from '/builds/gitlab/gitlab-ee/app/assets/javascripts/repository/components/table/index.vue?vue3';
export default {
    name: 'TreeContent',
    i18n,
    components: {
        FileTable,
        FilePreview
    },
    mixins: [
        getRefMixin
    ],
    apollo: {
        projectPath: {
            query: projectPathQuery
        }
    },
    inject: [
        'refType'
    ],
    props: {
        path: {
            type: String,
            required: false,
            default: '/'
        },
        loadingPath: {
            type: String,
            required: false,
            default: ''
        }
    },
    data () {
        return {
            commits: [],
            projectPath: '',
            nextPageCursor: '',
            pagesLoaded: 1,
            entries: {
                trees: [],
                submodules: [],
                blobs: []
            },
            isLoadingFiles: false,
            clickedShowMore: false,
            fetchCounter: 0
        };
    },
    computed: {
        totalEntries () {
            return Object.values(this.entries).flat().length;
        },
        readme () {
            return readmeFile(this.entries.blobs);
        },
        pageLimitReached () {
            return this.totalEntries / this.pagesLoaded >= TREE_PAGE_LIMIT;
        },
        hasShowMore () {
            return !this.clickedShowMore && this.pageLimitReached;
        }
    },
    watch: {
        path (newPath, oldPath) {
            // Skip on initial mount
            if (!oldPath) return;
            // Refetch when path changes
            if (newPath !== oldPath) {
                this.entries.trees = [];
                this.entries.submodules = [];
                this.entries.blobs = [];
                this.nextPageCursor = '';
                resetRequestedCommits();
                this.fetchFiles();
            }
        }
    },
    mounted () {
        eventHub.$on(LOCK_UPDATED_EVENT, this.refreshCommitData);
        // We need to wait for `ref` and `projectPath` to be set
        this.$nextTick(()=>{
            resetRequestedCommits();
            this.fetchFiles();
        });
    },
    beforeDestroy () {
        eventHub.$off(LOCK_UPDATED_EVENT, this.refreshCommitData);
    },
    methods: {
        fetchFiles () {
            const originalPath = this.path;
            this.isLoadingFiles = true;
            return this.$apollo.query({
                query: paginatedTreeQuery,
                variables: {
                    projectPath: this.projectPath,
                    ref: this.ref,
                    refType: getRefType(this.refType),
                    path: originalPath,
                    nextPageCursor: this.nextPageCursor,
                    pageSize: TREE_PAGE_SIZE
                }
            }).then(({ data })=>{
                if (data.errors) throw data.errors;
                if (!data?.project?.repository || originalPath !== this.path) return;
                const { project: { repository: { paginatedTree: { pageInfo } } } } = data;
                this.isLoadingFiles = false;
                this.entries = Object.keys(this.entries).reduce((acc, key)=>({
                        ...acc,
                        [key]: this.normalizeData(key, data.project.repository.paginatedTree.nodes[0][key])
                    }), {});
                if (pageInfo?.hasNextPage) {
                    this.nextPageCursor = pageInfo.endCursor;
                    this.fetchCounter += 1;
                    if (!this.pageLimitReached || this.clickedShowMore) {
                        this.fetchFiles();
                        this.clickedShowMore = false;
                    }
                }
            }).catch((error)=>{
                let gitalyUnavailableError;
                if (error.graphQLErrors) {
                    gitalyUnavailableError = error.graphQLErrors.find((e)=>e?.extensions?.code === GITALY_UNAVAILABLE_CODE);
                }
                const message = gitalyUnavailableError ? this.$options.i18n.gitalyError : this.$options.i18n.generalError;
                createAlert({
                    message,
                    captureError: true
                });
            });
        },
        normalizeData (key, data) {
            return this.entries[key].concat(data.nodes);
        },
        handleRowAppear (rowNumber) {
            if (window.gon?.show_commit_columns === false) {
                return;
            }
            if (isRequested(rowNumber)) {
                return;
            }
            // Assume we are loading from the top and greedily choose offsets in multiples of COMMIT_BATCH_SIZE to minimize number of requests
            this.loadCommitData(rowNumber - rowNumber % COMMIT_BATCH_SIZE);
        },
        loadCommitData (rowNumber) {
            loadCommits(this.projectPath, this.path, this.ref, rowNumber, this.refType).then(this.setCommitData).catch(()=>{});
        },
        setCommitData (data) {
            this.commits = this.commits.concat(data);
        },
        async refreshCommitData () {
            if (window.gon?.show_commit_columns === false) {
                return;
            }
            this.commits = await reloadCommits(this.projectPath, this.path, this.ref, this.refType);
        },
        handleShowMore () {
            this.clickedShowMore = true;
            this.pagesLoaded += 1;
            this.fetchFiles();
        }
    }
};
